import { Box, CircularProgress, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import moment from 'moment';
import '../eslFonts.css';
import hbscanQRCodeUrl, { ReactComponent as HBscanQRCode } from '../../../assets/images/esl/hbscanQRcodePlaceholder.svg';
import IEslTemplateItem from '../../../models/esl/eslTemplateElement';
import { EEslTemplateElementType } from '../../../models/enums/eslTemplateType';
import { EEslDatetimeLanguage } from '../../../models/enums/eslDatetimeLanguage';
import { useEffect, useRef, useState } from 'react';
import { colorForScreen, quantizeImageForScreen } from '../quantizeImage';
import { useEslPreviewMode } from '../EslPreviewModeContext';

type SingleLanguage = EEslDatetimeLanguage.English | EEslDatetimeLanguage.French | EEslDatetimeLanguage.Spanish;

const datetimeMomentLocale: Map<SingleLanguage, string> = new Map([
  [EEslDatetimeLanguage.English, "en"],
  [EEslDatetimeLanguage.French, "fr"],
  [EEslDatetimeLanguage.Spanish, "es"],
]);

const datetimeDateFormat: Map<SingleLanguage, string> = new Map([
  [EEslDatetimeLanguage.English, "MMM DD, YYYY"],
  [EEslDatetimeLanguage.French, "DD MMM YYYY"],
  [EEslDatetimeLanguage.Spanish, "DD MMM YYYY"],
]);

const datetimeTimeFormat: Map<SingleLanguage, string> = new Map([
  [EEslDatetimeLanguage.English, "h:mm A"],
  [EEslDatetimeLanguage.French, "HH [h] mm"],
  [EEslDatetimeLanguage.Spanish, "HH:mm"],
]);

const BILINGUAL_TIME_FORMAT = "HH:mm";

const getDateLanguagesInOrder = (language: EEslDatetimeLanguage): SingleLanguage[] => {
  if (language === EEslDatetimeLanguage.BilingualFrenchEnglish) return [EEslDatetimeLanguage.French, EEslDatetimeLanguage.English];
  if (language === EEslDatetimeLanguage.BilingualEnglishFrench) return [EEslDatetimeLanguage.English, EEslDatetimeLanguage.French];

  return [language as SingleLanguage];
};

const formatDate = (language: SingleLanguage): string => {
  const locale = datetimeMomentLocale.get(language) || "fr";
  const format = datetimeDateFormat.get(language) || "DD MMM YYYY";

  return moment().locale(locale).format(format).replace('.', '').toUpperCase();
};

const measureBoldTextWidth = (text: string, fontSize: number): number => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return text.length * fontSize * 0.55;

  ctx.font = `bold ${fontSize}px 'Open Sans Local', sans-serif`;
  return ctx.measureText(text).width;
};

interface IRenderedElementProps {
  element: IEslTemplateItem;
  onResize?: (width: number, height: number) => void;
}

const RenderedElement = ({ element, onResize }: IRenderedElementProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const { mode } = useEslPreviewMode();

  const sourceImageSrc = element.type === EEslTemplateElementType.Image ? element.imageSrc : undefined;
  const [quantizedImageSrc, setQuantizedImageSrc] = useState<string | undefined>(sourceImageSrc);
  const [isQuantizingImage, setIsQuantizingImage] = useState(false);

  useEffect(() => {
    if (!sourceImageSrc || mode === 'design') {
      setQuantizedImageSrc(sourceImageSrc);
      setIsQuantizingImage(false);
      return;
    }
    let cancelled = false;
    setQuantizedImageSrc(sourceImageSrc);
    setIsQuantizingImage(true);
    quantizeImageForScreen(sourceImageSrc, mode).then((url) => {
      if (cancelled) return;
      setQuantizedImageSrc(url);
      setIsQuantizingImage(false);
    });
    return () => { cancelled = true; };
  }, [sourceImageSrc, mode]);

  const [quantizedQrUrl, setQuantizedQrUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (element.type !== EEslTemplateElementType.ServiceRequestQRCode || mode === 'design') {
      setQuantizedQrUrl(undefined);
      return;
    }
    let cancelled = false;
    quantizeImageForScreen(hbscanQRCodeUrl, mode).then((url) => {
      if (!cancelled) setQuantizedQrUrl(url);
    });
    return () => { cancelled = true; };
  }, [element.type, mode]);

  const displayColor = (target: string | undefined) => {
    if (!target) return target;
    if (mode === 'design') {
      // Only white stays as pure white in design mode (so it's visible on the
      // white canvas). Every other colour always shows its ESL-perceived hue.
      return target.toUpperCase() === '#FFFFFF' ? '#FFFFFF' : colorForScreen(target, 'colorScreen');
    }
    return colorForScreen(target, mode);
  };

  useEffect(() => {
    if (!onResize) return;
    if (element.type !== EEslTemplateElementType.Text && element.type !== EEslTemplateElementType.MultilineText) return;
    if (!textRef.current) return;

    const { width, height } = textRef.current.getBoundingClientRect();
    onResize(Math.ceil(width), Math.ceil(height));
  }, [element.textContent, element.style, element.type]);

  const elementStyle = {
    width: '100%',
    height: '100%',
    overflow: "hidden",
    fontFamily: "'Open Sans Local', sans-serif",
    fontWeight: element.style.isBold ? 'bold' : 'normal',
    fontStyle: element.style.isItalic ? 'italic' : 'normal',
    textDecoration: element.style.isUnderline ? 'underline' : 'none',
    fontSize: element.style.fontSize,
    textAlign: element.style.textAlign,
    color: displayColor(element.style.color),
    backgroundColor: displayColor(element.style.backgroundColor),
  };

  const elementMap = new Map<EEslTemplateElementType, JSX.Element>([
    [EEslTemplateElementType.Text, (
      <Typography sx={{ ...elementStyle, display: 'inline-block' }}>
        <span ref={textRef} style={{ whiteSpace: 'nowrap' }}>{element.textContent}</span>
      </Typography>
    )],
    [EEslTemplateElementType.MultilineText, (
      <Typography sx={{ ...elementStyle, whiteSpace: "pre-wrap", lineHeight: 1.2 }}>
        {element.textContent}
      </Typography>
    )],
    [EEslTemplateElementType.Image, element.imageSrc ? (
      <Box sx={{ ...elementStyle, position: 'relative' }}>
        <img
          src={quantizedImageSrc ?? element.imageSrc}
          alt="Template Image"
          style={{
            width: '100%',
            height: '100%',
            imageRendering: mode === 'design' ? 'auto' : 'pixelated',
            opacity: isQuantizingImage ? 0.4 : 1,
            transition: 'opacity 150ms ease',
          }}
          draggable={false}
          crossOrigin="anonymous"
        />
        {isQuantizingImage && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={20} thickness={5} />
          </Box>
        )}
      </Box>
    ) : (
      <Box sx={{ ...elementStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ImageIcon sx={{ width: "100%", fontSize: "100vw" }} />
      </Box>
    )],
    [EEslTemplateElementType.Line, <Box sx={elementStyle} />],
    [EEslTemplateElementType.Rectangle, <Box sx={elementStyle} />],
    [EEslTemplateElementType.Circle, <Box sx={{ ...elementStyle, borderRadius: '100%' }} />],
    [EEslTemplateElementType.LastCleaningDatetime, (() => {
      const language = element.language || EEslDatetimeLanguage.French;
      const dateLanguages = getDateLanguagesInOrder(language);
      const isBilingual = dateLanguages.length > 1;
      const timeText = isBilingual
        ? moment().format(BILINGUAL_TIME_FORMAT)
        : moment().locale(datetimeMomentLocale.get(language as SingleLanguage) || "fr").format(datetimeTimeFormat.get(language as SingleLanguage) || "HH[h]mm");
      const dateTexts = dateLanguages.map(formatDate);
      const datetimeColor = displayColor(element.style.color) || displayColor('#000000');

      // Bilingual splits the height across one more line, so shrink the
      // base sizes to keep the block comfortably inside the element.
      const timeBaseRatio = isBilingual ? 0.5 : 0.6;
      const dateBaseRatio = isBilingual ? 0.36 : 0.4;
      let timeFontSize = element.dimensions.height * timeBaseRatio;
      let dateFontSize = timeFontSize * dateBaseRatio;
      timeFontSize *= Math.min(1, element.dimensions.width / measureBoldTextWidth(timeText, timeFontSize));
      const widestDate = Math.max(...dateTexts.map((text) => measureBoldTextWidth(text, dateFontSize)));
      dateFontSize *= Math.min(1, element.dimensions.width / widestDate);

      return (
        <Box sx={{
          ...elementStyle,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}>
          <Typography sx={{ fontSize: timeFontSize, fontFamily: "'Open Sans Local', sans-serif", fontWeight: 'bold', lineHeight: 1, color: datetimeColor, whiteSpace: 'nowrap' }}>{timeText}</Typography>
          {dateTexts.map((text, index) => (
            <Typography key={index} sx={{ fontSize: dateFontSize, fontFamily: "'Open Sans Local', sans-serif", fontWeight: 'bold', lineHeight: 1, color: datetimeColor, whiteSpace: 'nowrap' }}>{text}</Typography>
          ))}
        </Box>
      );
    })()],
    [EEslTemplateElementType.ServiceRequestQRCode, quantizedQrUrl ? (
      <img src={quantizedQrUrl} alt="QR Code" style={{ ...elementStyle, width: '100%', height: '100%' }} draggable={false} />
    ) : (
      <HBscanQRCode style={{ ...elementStyle, width: '100%', height: '100%' }} />
    )],
  ]);

  return elementMap.get(element.type) || null;
};

export default RenderedElement;
